import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Plus, PaperPlaneTilt, Trash, X, ClockClockwise, Link as LinkIcon, NotePencil } from "@phosphor-icons/react";
import StatusBadge from "@/components/StatusBadge";
import { format } from "date-fns";

const toLocalInput = (d) => {
  const pad = (n) => `${n}`.padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const Shifts = () => {
  const [shifts, setShifts] = useState([]);
  const [guards, setGuards] = useState([]);
  const [sites, setSites] = useState([]);
  const [open, setOpen] = useState(false);
  const [filter, setFilter] = useState("all");
  const [editingNoteId, setEditingNoteId] = useState(null);
  const [noteDraft, setNoteDraft] = useState("");

  const [form, setForm] = useState(() => ({
    guard_id: "",
    site_id: "",
    start_time: toLocalInput(new Date(Date.now() + 15 * 60 * 1000)),
    end_time: toLocalInput(new Date(Date.now() + 8 * 60 * 60 * 1000)),
    check_in_lead_minutes: 10,
    notes: "",
  }));

  const load = async () => {
    const [s, g, st] = await Promise.all([
      api.get("/shifts"),
      api.get("/guards"),
      api.get("/sites"),
    ]);
    setShifts(s.data);
    setGuards(g.data);
    setSites(st.data);
  };
  useEffect(() => {
    const initial = setTimeout(load, 0);
    const id = setInterval(load, 10000);
    return () => { clearTimeout(initial); clearInterval(id); };
  }, []);

  const create = async (e) => {
    e.preventDefault();
    if (!form.guard_id || !form.site_id) { toast.error("Choose guard and site"); return; }
    try {
      const payload = {
        ...form,
        start_time: new Date(form.start_time).toISOString(),
        end_time: new Date(form.end_time).toISOString(),
        check_in_lead_minutes: parseInt(form.check_in_lead_minutes, 10) || 10,
      };
      await api.post("/shifts", payload);
      toast.success("Shift scheduled");
      setOpen(false);
      setForm({ ...form, notes: "" });
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Schedule failed");
    }
  };

  const sendPing = async (id) => {
    try {
      await api.post(`/shifts/${id}/send-checkin`);
      toast.success("Check-in ping sent");
      await load();
    } catch (err) { toast.error("Failed to send ping"); }
  };

  const remove = async (s) => {
    if (!window.confirm("Delete this shift?")) return;
    await api.delete(`/shifts/${s.shift_id}`);
    toast.success("Shift deleted");
    await load();
  };

  const copyLink = async (s) => {
    const url = `${window.location.origin}/checkin/${s.check_in_token}`;
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Magic-link copied to clipboard");
    } catch {
      toast.message(url);
    }
  };

  const openNote = (s) => {
    setEditingNoteId(s.shift_id);
    setNoteDraft(s.notes || "");
  };

  const saveNote = async (s) => {
    try {
      await api.patch(`/shifts/${s.shift_id}`, { notes: noteDraft });
      toast.success("Note saved");
      setEditingNoteId(null);
      await load();
    } catch {
      toast.error("Failed to save note");
    }
  };

  const filtered = filter === "all" ? shifts : shifts.filter((s) => s.status === filter);

  return (
    <div className="space-y-6" data-testid="shifts-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="label-mono">// SCHEDULE</div>
          <h1 className="font-display text-3xl sm:text-4xl font-black tracking-tight mt-1">Shifts</h1>
          <p className="text-sm text-zinc-500 mt-1">Create shifts and dispatch automated check-in pings.</p>
        </div>
        <button data-testid="add-shift-btn" onClick={() => setOpen(true)} className="flex items-center gap-2 bg-[#007AFF] hover:bg-[#0066d6] px-4 py-2.5 text-xs uppercase tracking-wider font-semibold">
          <Plus size={14} weight="bold" /> Schedule Shift
        </button>
      </div>

      <div className="flex gap-px bg-white/10 w-fit">
        {[
          ["all", "All"],
          ["scheduled", "Scheduled"],
          ["ping_sent", "Pinged"],
          ["arrived", "On Post"],
          ["late", "Late"],
          ["no_show", "No Show"],
        ].map(([k, lbl]) => (
          <button
            key={k}
            data-testid={`filter-${k}`}
            onClick={() => setFilter(k)}
            className={`px-3 py-2 text-xs uppercase tracking-wider ${filter===k ? "bg-white/10 text-white" : "bg-[#0A0A0A] text-zinc-400 hover:text-white"}`}
          >{lbl}</button>
        ))}
      </div>

      <div className="tac-card overflow-hidden">
        {filtered.length === 0 ? (
          <div className="p-12 text-center">
            <div className="label-mono mb-2">// NO SHIFTS</div>
            <p className="text-sm text-zinc-400">Schedule a shift to begin dispatching.</p>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="text-zinc-500 border-b border-white/10">
              <tr>
                <th className="text-left px-4 py-3 label-mono">Guard</th>
                <th className="text-left px-4 py-3 label-mono">Site</th>
                <th className="text-left px-4 py-3 label-mono">Window</th>
                <th className="text-left px-4 py-3 label-mono">Lead</th>
                <th className="text-left px-4 py-3 label-mono">Status</th>
                <th className="text-right px-4 py-3 label-mono">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((s) => (
                <React.Fragment key={s.shift_id}>
                <tr className="border-b border-white/5 hover:bg-white/[0.03]" data-testid={`shift-row-${s.shift_id}`}>
                  <td className="px-4 py-3">
                    <div className="font-medium">{s.guard_name}</div>
                    <div className="text-xs text-zinc-500 font-mono">{s.guard_phone || "—"}</div>
                  </td>
                  <td className="px-4 py-3">
                    <div>{s.site_name}</div>
                    <div className="text-xs text-zinc-500">{s.site_address}</div>
                  </td>
                  <td className="px-4 py-3 font-mono text-xs">
                    <div>{format(new Date(s.start_time), "MMM d · HH:mm")}</div>
                    <div className="text-zinc-500">→ {format(new Date(s.end_time), "HH:mm")}</div>
                  </td>
                  <td className="px-4 py-3 font-mono text-xs text-zinc-300">T-{s.check_in_lead_minutes}m</td>
                  <td className="px-4 py-3"><StatusBadge status={s.status} /></td>
                  <td className="px-4 py-3 text-right">
                    <div className="inline-flex gap-1">
                      <button data-testid={`send-ping-row-${s.shift_id}`} onClick={()=>sendPing(s.shift_id)} className="inline-flex items-center gap-1 border border-white/15 hover:border-[#007AFF] hover:text-[#007AFF] px-2 py-1 text-[0.65rem] uppercase tracking-wider">
                        <PaperPlaneTilt size={12}/> Ping
                      </button>
                      <button data-testid={`copy-link-${s.shift_id}`} onClick={()=>copyLink(s)} className="inline-flex items-center gap-1 border border-white/15 hover:border-[#2BD4D9] hover:text-[#2BD4D9] px-2 py-1 text-[0.65rem] uppercase tracking-wider">
                        <LinkIcon size={12}/> Link
                      </button>
                      <button data-testid={`edit-note-${s.shift_id}`} onClick={()=>openNote(s)} title={s.notes || "Add note"} className={`border border-white/15 hover:bg-white/5 p-2 ${s.notes ? "text-[#2BD4D9]" : "text-zinc-500"}`}><NotePencil size={12}/></button>
                      <button data-testid={`delete-shift-${s.shift_id}`} onClick={()=>remove(s)} className="border border-white/15 hover:bg-red-500/10 hover:border-red-500/40 hover:text-red-400 p-2"><Trash size={12}/></button>
                    </div>
                  </td>
                </tr>
                {editingNoteId === s.shift_id ? (
                  <tr className="border-b border-white/5 bg-white/[0.02]">
                    <td colSpan={6} className="px-4 py-3">
                      <div className="flex gap-2 items-start">
                        <NotePencil size={16} className="text-[#2BD4D9] mt-2 shrink-0"/>
                        <textarea
                          data-testid={`note-input-${s.shift_id}`}
                          value={noteDraft}
                          onChange={(e)=>setNoteDraft(e.target.value)}
                          placeholder="Incident notes, callouts, coverage details..."
                          rows={2}
                          className="flex-1 bg-[#0A0A0A] border border-white/10 focus:border-[#2BD4D9] outline-none px-3 py-2 text-sm"
                          autoFocus
                        />
                        <div className="flex flex-col gap-1">
                          <button data-testid={`save-note-${s.shift_id}`} onClick={()=>saveNote(s)} className="px-3 py-1.5 bg-[#007AFF] hover:bg-[#0066d6] text-xs uppercase tracking-wider font-semibold">Save</button>
                          <button onClick={()=>setEditingNoteId(null)} className="px-3 py-1.5 border border-white/15 text-xs uppercase tracking-wider">Cancel</button>
                        </div>
                      </div>
                    </td>
                  </tr>
                ) : s.notes ? (
                  <tr className="border-b border-white/5 bg-white/[0.04] border-l-2 border-l-[#2BD4D9]/30">
                    <td colSpan={6} className="px-4 pb-3 pt-2">
                      <div className="text-xs text-zinc-300 italic pl-7" data-testid={`note-display-${s.shift_id}`}>
                        <span className="text-zinc-500">note:</span> {s.notes}
                      </div>
                    </td>
                  </tr>
                ) : null}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {open && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4" data-testid="shift-modal">
          <div className="bg-[#121212] border border-white/10 max-w-lg w-full">
            <div className="flex items-center justify-between border-b border-white/10 px-5 py-3">
              <div className="label-mono">// NEW SHIFT</div>
              <button onClick={() => setOpen(false)} className="text-zinc-500 hover:text-white"><X size={16}/></button>
            </div>
            <form onSubmit={create} className="p-5 space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="label-mono block mb-1">Guard</label>
                  <select data-testid="shift-guard-select" required value={form.guard_id} onChange={(e)=>setForm({...form, guard_id:e.target.value})} className="w-full bg-[#0A0A0A] border border-white/10 focus:border-[#007AFF] outline-none px-3 py-2 text-sm">
                    <option value="">Select guard</option>
                    {guards.map((g)=>(<option key={g.guard_id} value={g.guard_id}>{g.name}</option>))}
                  </select>
                </div>
                <div>
                  <label className="label-mono block mb-1">Site</label>
                  <select data-testid="shift-site-select" required value={form.site_id} onChange={(e)=>setForm({...form, site_id:e.target.value})} className="w-full bg-[#0A0A0A] border border-white/10 focus:border-[#007AFF] outline-none px-3 py-2 text-sm">
                    <option value="">Select site</option>
                    {sites.map((s)=>(<option key={s.site_id} value={s.site_id}>{s.name}</option>))}
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="label-mono block mb-1">Start</label>
                  <input data-testid="shift-start" type="datetime-local" required value={form.start_time} onChange={(e)=>setForm({...form, start_time:e.target.value})} className="w-full bg-[#0A0A0A] border border-white/10 focus:border-[#007AFF] outline-none px-3 py-2 text-sm font-mono"/>
                </div>
                <div>
                  <label className="label-mono block mb-1">End</label>
                  <input data-testid="shift-end" type="datetime-local" required value={form.end_time} onChange={(e)=>setForm({...form, end_time:e.target.value})} className="w-full bg-[#0A0A0A] border border-white/10 focus:border-[#007AFF] outline-none px-3 py-2 text-sm font-mono"/>
                </div>
              </div>
              <div>
                <label className="label-mono block mb-1 flex items-center gap-1"><ClockClockwise size={12}/> Check-in Lead (minutes before start)</label>
                <input data-testid="shift-lead" type="number" min="0" max="120" value={form.check_in_lead_minutes} onChange={(e)=>setForm({...form, check_in_lead_minutes:e.target.value})} className="w-full bg-[#0A0A0A] border border-white/10 focus:border-[#007AFF] outline-none px-3 py-2 text-sm font-mono"/>
              </div>
              <div>
                <label className="label-mono block mb-1">Notes</label>
                <textarea data-testid="shift-notes" value={form.notes} onChange={(e)=>setForm({...form, notes:e.target.value})} rows={2} className="w-full bg-[#0A0A0A] border border-white/10 focus:border-[#007AFF] outline-none px-3 py-2 text-sm"/>
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <button type="button" onClick={()=>setOpen(false)} className="px-3 py-2 border border-white/15 text-xs uppercase tracking-wider">Cancel</button>
                <button data-testid="save-shift-btn" type="submit" className="px-3 py-2 bg-[#007AFF] hover:bg-[#0066d6] text-xs uppercase tracking-wider font-semibold">Schedule</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Shifts;
